'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { LanguageSelect } from './language-select';
import { usePathname } from 'next/navigation';
import { Button } from '../ui/button';
import { LuMenu, LuX } from 'react-icons/lu';
import { HeaderLanguageMultistore } from './header-language-multistore';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link href={category.slug ?? '#'} className="flex-1" prefetchOnHover>
          <SheetClose
            className={cn(
              'text-left font-semibold',
              layer === 1 && 'text-sm font-medium',
              layer && layer > 1 && 'text-xs font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const pathname = usePathname();

  const isHomepage = pathname === '/';

  const contacts = [
    {
      image: '/images/icons/mobile.svg',
      label: t('text800'),
      value: generalLayout?.contact?.phone || '',
      href: `tel:${generalLayout?.contact?.phone}`,
    },
    {
      image: '/images/icons/email.svg',
      label: t('text569'),
      value: generalLayout?.contact?.email || '',
      href: `mailto:${generalLayout?.contact?.email}`,
    },
  ];

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu"
          asChild
          className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <Button
            size={'icon'}
            variant={isHomepage ? 'alternative-default' : 'default'}
            aria-label={t('text2309')}
          >
            <LuMenu size={20} aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetPortal>
          <SheetOverlay className="bg-black/30 backdrop-blur-none" />
          <SheetContent
            side={'left'}
            className={cn(
              'flex w-full max-w-[350px] flex-col items-start justify-start gap-6 border-none p-6 pt-16 shadow-none',
              'ea-mobile-sidebar__content',
              props.className?.content,
            )}
            closeIcon={{
              wrapper: 'right-4 top-4',
              icon: 'text-black/60 h-6 w-6 font-bold',
            }}
          >
            <SheetTitle className="sr-only">{t('text2309')}</SheetTitle>
            {!!props.menu && (
              <div className="flex w-full flex-col items-start justify-start gap-2">
                <SheetClose asChild>
                  <Link
                    href={ROUTES.CATEGORIES()}
                    prefetchOnHover
                    className="text-lg font-semibold text-primary"
                  >
                    {t('text2309')}
                  </Link>
                </SheetClose>

                {props.menu.map((item, index) => (
                  <SheetClose asChild key={index}>
                    <Link
                      href={getSafeHref(item?.path ?? '')}
                      prefetchOnHover
                      className="text-lg text-black/70"
                    >
                      {item.name}
                    </Link>
                  </SheetClose>
                ))}
              </div>
            )}
            <Separator className="bg-black/10" />
            <div className="flex w-full flex-col items-start justify-start gap-4">
              {contacts.map((contact, index) => (
                <ContactCard
                  key={index}
                  image={contact.image}
                  label={contact.label}
                  value={contact.value}
                  href={contact.href}
                />
              ))}
            </div>
            <Separator className="bg-black/10" />
            <div className="flex w-full items-center justify-end">
              <HeaderLanguageMultistore isHomepage={false} />
            </div>
          </SheetContent>
        </SheetPortal>
      </Sheet>
    </>
  );
};

export default MobileSidebar;

function ContactCard({
  image,
  label,
  value,
  href,
}: {
  image: string;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <Link
      prefetchOnHover
      prefetch={false}
      target="_blank"
      href={href}
      className="flex w-fit items-center justify-start gap-6"
    >
      <div className="flex aspect-square w-full min-w-12 max-w-12 items-center justify-center rounded-[12px] bg-black/5 sm:max-w-14 lg:min-w-14 lg:max-w-12">
        <Image src={image} alt={label} width={0} height={0} className="h-6 w-6 sm:w-8 lg:w-8" />
      </div>
      <div className="flex flex-col items-start justify-start gap-1">
        <p className="whitespace-nowrap text-sm font-light sm:text-base">{label}</p>
        <p className="whitespace-nowrap text-base font-medium tracking-tight text-primary sm:text-lg lg:text-xl">
          {value}
        </p>
      </div>
    </Link>
  );
}
